import cn from "classnames";

import { brown } from "@/app/font";
import {
  COMPARE_COMPETITOR_TABLE_APPEARANCES,
  type CompareCompetitorTableAppearance,
} from "@/sanity/defaults/compareCompetitorTable.defaults";

/** Brown bold base for competitor data columns. */
const DATA_COLUMN_BASE_CLASS = cn(brown.className, "font-bold");

/** Highlighted brand column text. */
export const HIGHLIGHTED_DATA_COLUMN_TEXT_CLASS = cn(
  DATA_COLUMN_BASE_CLASS,
  "text-secondary-100",
);

/** Non-highlighted competitor column text. */
export const COMPETITOR_DATA_COLUMN_TEXT_CLASS = cn(
  DATA_COLUMN_BASE_CLASS,
  "text-greyscale-body",
);

export function resolveAppearance(
  appearance: string | null | undefined,
): CompareCompetitorTableAppearance {
  return appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt
    ? COMPARE_COMPETITOR_TABLE_APPEARANCES.adt
    : COMPARE_COMPETITOR_TABLE_APPEARANCES.adtBlu;
}

export function competitorColumnIndexes(
  columnCount: number,
  highlightIndex: number,
): number[] {
  return Array.from({ length: columnCount }, (_, index) => index).filter(
    (index) => index !== highlightIndex,
  );
}

export function usesMobileCompetitorSelect(
  appearance: CompareCompetitorTableAppearance,
  highlightIndex: number,
  competitorCount: number,
): boolean {
  return (
    appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt &&
    highlightIndex >= 0 &&
    competitorCount > 1
  );
}

export function competitorCellTextVariant(
  appearance: CompareCompetitorTableAppearance,
  columnIndex: number,
  highlightIndex: number,
): "body2" | "body4" {
  return appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt &&
    columnIndex === highlightIndex
    ? "body2"
    : "body4";
}

export function dataColumnTextClass(
  columnIndex: number,
  highlightIndex: number,
  appearance: CompareCompetitorTableAppearance = COMPARE_COMPETITOR_TABLE_APPEARANCES.adtBlu,
): string {
  if (appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt) {
    return "text-greyscale-headline";
  }

  return columnIndex === highlightIndex
    ? HIGHLIGHTED_DATA_COLUMN_TEXT_CLASS
    : COMPETITOR_DATA_COLUMN_TEXT_CLASS;
}

/** Thin border between data rows (first row sits under the header rule instead). */
const ROW_DIVIDER = "border-t border-greyscale-strength/20";

export function rowDividerClass(isFirstRow: boolean): string {
  return isFirstRow ? "" : ROW_DIVIDER;
}

/** Highlight fill for our own brand column, rounded at the top and bottom of the table. */
export function columnHighlightClass(
  columnIndex: number,
  highlightIndex: number,
  rowPosition: "header" | "body" | "last",
  appearance: CompareCompetitorTableAppearance = COMPARE_COMPETITOR_TABLE_APPEARANCES.adtBlu,
): string {
  if (highlightIndex < 0 || columnIndex !== highlightIndex) return "";
  return cn(
    appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt
      ? "bg-greyscale-background"
      : "bg-primary-100",
    rowPosition === "header" && "rounded-t-xl",
    rowPosition === "last" && "rounded-b-xl",
  );
}
